import React, { createContext, useState, useContext, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { track, userTypeFromRole } from '@/lib/analytics';
import { appParams } from '@/lib/app-params';
import { createAxiosClient } from '@base44/sdk/dist/utils/axios-client';

const AuthContext = createContext();

// --- OAuth first-time signup tracking -------------------------------------
// Fires the GA4 "sign_up" event for genuine first-time OAuth (Google) account
// creations. Lives here (not in PostLoginRedirect) because that component can
// unmount before its effect runs. AuthProvider is the root and never unmounts,
// so checkUserAuth is the reliable point where the user object first resolves.
const SIGNUP_WINDOW_MS = 300000; // 5 minutes
let oAuthSignupFiredThisSession = false;
let oAuthAcSyncFiredThisSession = false;

// Base44 returns created_date as a string with NO timezone, e.g.
// "2026-07-02T06:24:51.153000". new Date() would parse that as local time,
// making fresh UTC accounts appear hours old on UTC+offset clients. Append 'Z'
// so it is treated as UTC (the backend's actual storage zone).
function parseCreatedDateUtc(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  let str = String(value);
  const hasTz = /[zZ]$/.test(str) || /[+-]\d{2}:?\d{2}$/.test(str);
  if (!hasTz) str += 'Z';
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

function maybeFireOAuthSignup(user) {
  if (!user?.id) return;
  // Only the OAuth callback lands on /post-login; the email flow's
  // checkUserAuth call (from Register.jsx on /register) never does, so this
  // never doubles up with the email flow's own sign_up push.
  if (typeof window === 'undefined' || window.location?.pathname !== '/post-login') return;
  const created = parseCreatedDateUtc(user.created_date); // (a) corrected window
  if (!created) return;
  if (Date.now() - created.getTime() >= SIGNUP_WINDOW_MS) return;

  // GA4 sign_up event — per-session + localStorage dedupe.
  if (!oAuthSignupFiredThisSession) {
    let alreadyTracked = false; // (b) localStorage dedupe
    try { alreadyTracked = window.localStorage.getItem(`d24_signup_tracked_${user.id}`) === '1'; } catch (_) {}
    if (!alreadyTracked) {
      track('sign_up', { method: 'google' });
      oAuthSignupFiredThisSession = true;
      try { window.localStorage.setItem(`d24_signup_tracked_${user.id}`, '1'); } catch (_) {}
    }
  }

  // ActiveCampaign CRM sync — SEPARATE dedupe so a prior session that fired
  // analytics but missed the CRM sync still syncs. Idempotent server-side.
  if (!oAuthAcSyncFiredThisSession) {
    let alreadySynced = false;
    try { alreadySynced = window.localStorage.getItem(`d24_ac_synced_${user.id}`) === '1'; } catch (_) {}
    if (!alreadySynced) {
      base44.functions.invoke('syncUserToActiveCampaign')
        .catch((e) => console.error('AC sync failed:', e));
      oAuthAcSyncFiredThisSession = true;
      try { window.localStorage.setItem(`d24_ac_synced_${user.id}`, '1'); } catch (_) {}
    }
  }
}
// --- end OAuth signup tracking --------------------------------------------

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [appPublicSettings, setAppPublicSettings] = useState(null); // Contains only { id, public_settings }

  useEffect(() => {
    checkAppState();
  }, []);

  const checkAppState = async () => {
    try {
      setIsLoadingPublicSettings(true);
      setAuthError(null);
      
      // First, check app public settings (with token if available)
      // This will tell us if auth is required, user not registered, etc.
      const appClient = createAxiosClient({
        baseURL: `/api/apps/public`,
        headers: {
          'X-App-Id': appParams.appId
        },
        token: appParams.token, // Include token if available
        interceptResponses: true
      });
      
      try {
        // Fire public-settings and user-auth in parallel — they're independent.
        // This removes the serial by-id → User/me dependency from the critical
        // path, cutting the critical chain from ~2.5s to ~1.2s on first load.
        const publicSettingsPromise = appClient.get(`/prod/public-settings/by-id/${appParams.appId}`);
        const authPromise = appParams.token
          ? checkUserAuth()
          : Promise.resolve().then(() => {
              setIsLoadingAuth(false);
              setIsAuthenticated(false);
              setAuthChecked(true);
            });

        const publicSettings = await publicSettingsPromise;
        setAppPublicSettings(publicSettings);
        await authPromise; // already running in parallel — resolves around the same time
        setIsLoadingPublicSettings(false);
      } catch (appError) {
        console.error('App state check failed:', appError);
        
        // Handle app-level errors
        if (appError.status === 403 && appError.data?.extra_data?.reason) {
          const reason = appError.data.extra_data.reason;
          if (reason === 'auth_required') {
            setAuthError({
              type: 'auth_required',
              message: 'Authentication required'
            });
          } else if (reason === 'user_not_registered') {
            setAuthError({
              type: 'user_not_registered',
              message: 'User not registered for this app'
            });
          } else {
            setAuthError({
              type: reason,
              message: appError.message
            });
          }
        } else {
          setAuthError({
            type: 'unknown',
            message: appError.message || 'Failed to load app'
          });
        }
        setIsLoadingPublicSettings(false);
        setIsLoadingAuth(false);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      setAuthError({
        type: 'unknown',
        message: error.message || 'An unexpected error occurred'
      });
      setIsLoadingPublicSettings(false);
      setIsLoadingAuth(false);
    }
  };

  const checkUserAuth = async () => {
    try {
      // Now check if the user is authenticated
      setIsLoadingAuth(true);
      const currentUser = await base44.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
      track("user_identified", { user_type: userTypeFromRole(currentUser.role) });
      maybeFireOAuthSignup(currentUser);
      setIsLoadingAuth(false);
      setAuthChecked(true);

      // Apply any pending reseller promotion (admin invited a non-registered
      // user; promotion runs once the user accepts and logs in). Fire-and-forget.
      if (currentUser?.email) {
        base44.functions.invoke('applyPendingReseller', {})
          .then((r) => {
            const d = r.data || r;
            if (d?.success) {
              // Role changed — refresh the cached user so reseller UI renders.
              base44.auth.me().then(setUser).catch(() => {});
            }
          })
          .catch(() => {});
      }

      return currentUser;
    } catch (error) {
      setIsLoadingAuth(false);
      setIsAuthenticated(false);
      setAuthChecked(true);

      // Stale / expired token — clear it so we don't keep retrying with a bad one.
      // Don't force a redirect here; protected routes gate themselves via /login.
      // Detect 401/403 across the SDK and raw-axios error shapes (the status can
      // live on error.status, error.response.status, or error.response.data.status).
      const authStatus = Number(
        error?.status ?? error?.response?.status ?? error?.response?.data?.status ?? 0
      );
      if (authStatus === 401 || authStatus === 403) {
        try {
          window.localStorage.removeItem('base44_access_token');
          window.localStorage.removeItem('token');
        } catch (_) {}
      } else {
        // Unexpected (e.g. network) failure — keep the token, just log it.
        console.error('User auth check failed:', error);
      }
      return null;
    }
  };

  const logout = (shouldRedirect = true) => {
    setUser(null);
    setIsAuthenticated(false);
    
    if (shouldRedirect) {
      // Use the SDK's logout method which handles token cleanup and redirect
      base44.auth.logout(window.location.href);
    } else {
      // Just remove the token without redirect
      base44.auth.logout();
    }
  };

  const navigateToLogin = () => {
    // Use the SDK's redirectToLogin method
    base44.auth.redirectToLogin(window.location.href);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      authChecked,
      logout,
      navigateToLogin,
      checkUserAuth,
      checkAppState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};